import type { PageField, PageView } from '~/pages/AskRichardUnified/page/pageContract';
import { validatePageFilters } from '~/pages/AskRichardUnified/page/pageFilters';

import { metricText } from './charts';
import { CADENCES, COMPARISONS, FILTER_DEFINITIONS, type Kpis, filtersError, type LebtFilters, METRICS, SPLITS, TABS } from './model';

const VISIBLE_ROWS = 25;

const visibleKpis = (kpis: Kpis | null) => kpis && ({
  purchasers: metricText('Purchasers', kpis.purchasers),
  orders: metricText('Orders', kpis.orders),
  grossSales: metricText('Gross Sales', kpis.grossSales),
  margin: metricText('Margin', kpis.margin),
  grossSalesInclCredit: metricText('Gross Sales', kpis.grossSalesInclCredit),
});

type PageSection = {
  title: string;
  rows: Array<{ label: string; values: Record<string, number | string | null> }>;
  truncated: boolean;
};

export function lebtCommercialPageView(input: {
  ready: boolean;
  filters: LebtFilters;
  options: Record<string, Array<string>>;
  period: string;
  comparisonPeriod: string;
  current: Kpis | null;
  previous: Kpis | null;
  sections: Array<PageSection>;
}): PageView {
  const { filters, options } = input;
  const filterSchema: Record<string, PageField> = {
    tab: { type: 'enum', values: TABS.map((tab) => tab.value) },
  };
  if (filters.tab === 'orders') {
    Object.assign(filterSchema, {
      from: { type: 'date' },
      to: { type: 'date' },
      comparison: { type: 'enum', values: COMPARISONS.map((item) => item.value) },
      compareFrom: { type: 'date' },
      compareTo: { type: 'date' },
      cadence: { type: 'enum', values: CADENCES },
      metric: { type: 'enum', values: METRICS },
      split: { type: 'enum', values: SPLITS },
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => {
          const values = options[key] ?? [];
          return [key, values.length > 100
            ? { type: 'text', multiple: true, maxSelections: 200, maxLength: 4000 }
            : { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...values] }];
        }),
      ),
    });
  }
  return {
    title: 'LEBT Commercial Dashboard',
    ready: input.ready,
    dataMode: 'live analytics.lebt_sales_with_companies and exports_salesforce.lebt_business_activity_status',
    filters: {
      tab: filters.tab,
      from: filters.from,
      to: filters.to,
      comparison: filters.comparison,
      compareFrom: filters.compareFrom,
      compareTo: filters.compareTo,
      cadence: filters.cadence,
      metric: filters.metric,
      split: filters.split,
      ...Object.fromEntries(
        FILTER_DEFINITIONS.map(({ key }) => [
          key,
          filters.selections[key].join(','),
        ]),
      ),
    },
    filterSchema,
    activeSelections: Object.fromEntries(
      FILTER_DEFINITIONS.filter(({ key }) => filters.selections[key].length).map(({ key }) => [
        key,
        filters.selections[key],
      ]),
    ),
    period: input.period,
    comparisonPeriod: input.comparisonPeriod,
    summary: visibleKpis(input.current),
    comparisonSummary: visibleKpis(input.previous),
    visibleSections: input.sections.map((section) => ({
      title: section.title,
      totalRows: section.rows.length,
      rows: section.rows.slice(0, VISIBLE_ROWS).map((row) => ({ label: row.label, ...row.values })),
      truncated: section.truncated || section.rows.length > VISIBLE_ROWS,
    })),
    definitions: {
      source:
        'LEBT order items from lux-analytics-prod.analytics.lebt_sales_with_companies and registered businesses from exports_salesforce.lebt_business_activity_status, queried live; both rebuilt daily.',
      dates:
        'Sydney dates: the transaction date, or the order item time for items with no matched sale. The comparison is the same number of days before (PP), the same dates last year (LY) or custom dates.',
      measures:
        'Gross Sales sums gross_sales_aud (certified TTV). Margin sums margin_aud. Orders are distinct order_bk; Purchasers distinct businesses. Gross Sales incl GC and PC uses item_total for gift cards and purchased credit and is not TTV; Orders Summary and Weekly Summary use it, as the workbook does.',
      filters:
        'Product starts on the workbook selection that leaves out gift cards, purchased credit and unmatched items. Filters accept comma-separated listed values; an empty string selects all. Values containing commas cannot be edited here; activeSelections lists the effective values.',
    },
  };
}

export function editFromBridge(
  changes: Record<string, unknown>,
  view: PageView,
  filters: LebtFilters,
  options: Record<string, Array<string>>,
): Partial<LebtFilters> {
  const validation = { ...view.filterSchema };
  for (const { key } of FILTER_DEFINITIONS) {
    if (!validation[key]) continue;
    const values = [...new Set([...(options[key] ?? []), ...filters.selections[key]])];
    validation[key] = { type: 'enum', multiple: true, maxSelections: 200, values: ['', ...values] };
  }
  validatePageFilters(changes, validation);
  const next: Partial<LebtFilters> = {};
  for (const key of [
    'tab',
    'from',
    'to',
    'comparison',
    'compareFrom',
    'compareTo',
    'cadence',
    'metric',
    'split',
  ] as const) {
    if (key in changes) {
      Object.assign(next, { [key]: changes[key] });
    }
  }
  const selections = { ...filters.selections };
  let changed = false;
  for (const { key } of FILTER_DEFINITIONS) {
    if (key in changes) {
      const value = changes[key] as string;
      selections[key] = value.split(',').map((item) => item.trim()).filter(Boolean);
      changed = true;
    }
  }
  if (changed) {
    next.selections = selections;
  }
  const error = filtersError({ ...filters, ...next });
  if (error) throw new Error(error);
  return next;
}
